<template>
  <app-field
    v-slot="{ describedBy: fieldDescribedBy, invalid, hintTooltip, helpTooltip }"
    :hint-in-control-label="!fieldLabel"
    :label-for="inputId"
    :label="fieldLabel"
    :help="help"
    :hint="hint"
    :tooltip="tooltip"
    :error="error"
    :success="success"
    :required="required"
    :described-by="describedBy"
    :label-class="labelClass"
    :container-class="containerClass"
    :class="$attrs.class"
    :style="$attrs.style"
  >
    <div class="group inline-flex items-start gap-3" :class="choiceLabelClass">
      <label :for="inputId" class="relative mt-0.5 inline-flex shrink-0 items-center justify-center">
        <input
          v-bind="{ ...$attrs, class: undefined, style: undefined }"
          :id="inputId"
          ref="inputEl"
          type="checkbox"
          :name="name"
          :value="value"
          :checked="isChecked"
          :disabled="disabled"
          :required="required"
          :aria-describedby="mergeDescribedBy(fieldDescribedBy, $attrs['aria-describedby'], description && `${inputId}-description`)"
          :aria-invalid="invalid || $attrs['aria-invalid']"
          class="peer sr-only"
          @change="handleChange"
        />
        <span
          class="app-choice-box-theme inline-flex items-center justify-center peer-focus-visible:border-theme-focus peer-focus-visible:shadow-theme-focus"
          :class="boxClass"
        >
          <Icon
            :path="indeterminate ? mdiMinus : mdiCheck"
            class="transition duration-150"
            :class="[tokens.choiceIcon, isChecked || indeterminate ? 'scale-100 opacity-100' : 'scale-75 opacity-0']"
          />
        </span>
      </label>

      <span class="min-w-0">
        <span class="inline-flex items-center gap-2">
          <label v-if="label || $slots.label" :for="inputId" class="control-label block font-medium text-theme-text">
            <slot name="label">{{ label }}</slot>
          </label>
          <form-hint v-if="hintTooltip" :id="`${inputId}-hint`" :text="hintTooltip" :label="label" />
          <form-hint v-if="helpTooltip" :id="`${inputId}-help`" :text="helpTooltip" :label="label" />
        </span>
        <span v-if="description" :id="`${inputId}-description`" class="mt-0.5 block text-sm text-theme-text-secondary">
          {{ description }}
        </span>
      </span>
    </div>
  </app-field>
</template>

<script>
  import { mdiCheck, mdiMinus } from '@mdi/js';
  import { computed, onMounted, ref, watch } from 'vue';

  import FormHint from '@/components/ui/forms/FormHint.vue';
  import AppField from '@/components/ui/forms/AppField.vue';
  import Icon from '@/components/Icon.vue';
  import { getFormControlId, mergeDescribedBy, useFormControlStyles } from '@/composables/useFormControlStyles';

  export default {
    name: 'AppCheckbox',
    inheritAttrs: false,
    components: {
      AppField,
      FormHint,
      Icon,
    },
    props: {
      modelValue: { type: [Boolean, Array, String, Number], default: false },
      label: { type: String, default: '' },
      fieldLabel: { type: String, default: '' },
      description: { type: String, default: '' },
      help: { type: String, default: '' },
      hint: { type: String, default: '' },
      tooltip: { type: String, default: '' },
      error: { type: [String, Boolean, Array], default: false },
      success: { type: [String, Boolean], default: false },
      disabled: Boolean,
      required: Boolean,
      indeterminate: Boolean,
      name: { type: String, default: '' },
      id: { type: String, default: '' },
      value: { type: [String, Number, Boolean, Object], default: true },
      trueValue: { type: [String, Number, Boolean], default: true },
      falseValue: { type: [String, Number, Boolean], default: false },
      size: { type: String, default: 'md' },
      variant: { type: String, default: 'default' },
      describedBy: { type: String, default: '' },
      labelClass: { type: [String, Array, Object], default: '' },
      containerClass: { type: [String, Array, Object], default: '' },
    },
    emits: ['update:modelValue', 'change'],
    setup(props, { emit }) {
      const inputEl = ref(null);
      const generatedId = getFormControlId('app-checkbox');
      const { tokens, choiceLabelClass, hasError, hasSuccess } = useFormControlStyles(props);

      const inputId = computed(() => props.id || generatedId);
      const isChecked = computed(() => {
        if (Array.isArray(props.modelValue)) {
          return props.modelValue.includes(props.value);
        }

        return props.modelValue === props.trueValue || props.modelValue === true;
      });

      const boxClass = computed(() => [tokens.value.choice, {
        'app-choice-box-theme--disabled': props.disabled,
        'app-choice-box-theme--error': hasError.value,
        'app-choice-box-theme--success': hasSuccess.value,
        'app-choice-box-theme--checked': isChecked.value || props.indeterminate,
      }]);

      function syncIndeterminate() {
        if (inputEl.value) {
          inputEl.value.indeterminate = Boolean(props.indeterminate);
        }
      }

      function handleChange(event) {
        let nextValue;

        if (Array.isArray(props.modelValue)) {
          nextValue = [...props.modelValue];

          if (event.target.checked) {
            nextValue.push(props.value);
          } else {
            nextValue = nextValue.filter((item) => item !== props.value);
          }
        } else {
          nextValue = event.target.checked ? props.trueValue : props.falseValue;
        }

        emit('update:modelValue', nextValue);
        emit('change', event);
      }

      watch(() => props.indeterminate, syncIndeterminate);
      onMounted(syncIndeterminate);

      return {
        mdiCheck,
        mdiMinus,
        inputEl,
        tokens,
        choiceLabelClass,
        inputId,
        mergeDescribedBy,
        isChecked,
        boxClass,
        handleChange,
      };
    },
  };
</script>
